import React, { useState } from 'react';
import { Upload, Button, Card, message, Typography } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import api from '../../app/api';
import { AdminLayout } from '../../components/AdminLayout';
import { useTranslation } from '../../shared/hooks/useTranslation';
import style from './AdminDashboard.module.scss';

const { Title, Text } = Typography;

function extractFilename(contentDisposition: string | undefined, fallback: string): string {
  if (!contentDisposition) return fallback;
  // Берём filename="..." (без поддержки RFC 5987 — бэкенд отдаёт ASCII-имя).
  const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
  return match?.[1] || fallback;
}

export function AdminDashboard() {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { t } = useTranslation();

  const uploadProps: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error({ content: t.adminDashErrorExcelOnly, duration: 4 });
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error({ content: t.adminDashErrorSize, duration: 4 });
        return false;
      }
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error({ content: t.adminDashErrorSelect, duration: 3 });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', fileList[0] as any);

    try {
      const response = await api.post('/admin/upload-services', formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });

      if (response.status === 200) {
        const result = response.data;
        const details = result.details || {};
        const total = result.updatedCount || details.totalProcessed || 0;
        const ruCount = details.russianCount || 0;
        const kkCount = details.kazakhCount || 0;
        const enCount = details.englishCount || 0;

        const successMessage = result.message || `${t.adminDashSuccessHeader}

${t.adminDashSuccessUpdated} ${total} ${t.adminDashSuccessUpdatedSuffix}
${t.adminDashSuccessRu} ${ruCount} ${t.adminDashSuccessRecordsWord}
${t.adminDashSuccessKk} ${kkCount} ${t.adminDashSuccessRecordsWord}
${t.adminDashSuccessEn} ${enCount} ${t.adminDashSuccessRecordsWord}`;

        message.success({
          content: successMessage,
          duration: 8,
          style: { marginTop: '20vh' },
        });
        setFileList([]);
      } else {
        message.error(response.data?.error || t.adminDashErrorUpload);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || t.adminLoginErrorServer;
      message.error({
        content: `❌ ${t.adminDashErrorUpload}: ${errorMessage}`,
        duration: 6,
        style: { marginTop: '20vh' },
      });
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/admin/export-services', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const filename = extractFilename(
        response.headers?.['content-disposition'] as string | undefined,
        `services_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.success({ content: t.adminDashExportSuccess, duration: 3 });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || t.adminLoginErrorServer;
      message.error({ content: `❌ ${t.adminDashExportError}: ${errorMessage}`, duration: 5 });
    } finally {
      setExporting(false);
    }
  };

  const renderServicesContent = () => (
    <div className={style.content}>
      <div className={style.pageHeader}>
        <Title level={4} className={style.pageTitle}>{t.adminDashTitle}</Title>
        <Text type="secondary">{t.adminDashSubtitle}</Text>
      </div>

      <Card title={t.adminDashUploadHeading} className={style.block}>
        <div className={style.uploadBar}>
          <Upload {...uploadProps} maxCount={1}>
            <Button icon={<UploadOutlined />} className={style.btnSelect}>
              {t.adminDashSelectFile}
            </Button>
          </Upload>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            disabled={fileList.length === 0}
            loading={uploading}
            className={style.btnPrimary}
          >
            {uploading ? t.adminDashUploadingButton : t.adminDashUploadButton}
          </Button>
        </div>

        <div className={style.infoPanel}>
          <div className={style.infoCaption}>{t.adminDashFileStructureTitle}</div>
          <div className={style.infoGrid}>
            <div className={style.infoCol}>
              <div className={style.infoLine}>{t.adminDashSupportedFormats}</div>
              <div className={style.infoLine}>{t.adminDashMaxFileSize}</div>
            </div>
            <div className={style.infoCol}>
              <div className={style.infoHead}>{t.adminDashFileStructureSheets}</div>
              <div className={style.infoLine}>{t.adminDashSheet1}</div>
              <div className={style.infoLine}>{t.adminDashSheet2}</div>
              <div className={style.infoLine}>{t.adminDashSheet3}</div>
            </div>
            <div className={style.infoCol}>
              <div className={style.infoHead}>{t.adminDashColumnStructure}</div>
              <div className={style.infoLine}>{t.adminDashColumnsAC}</div>
              <div className={style.infoLine}>{t.adminDashColumnsDF}</div>
              <div className={style.infoLine}>{t.adminDashColumnsGI}</div>
            </div>
          </div>
        </div>
      </Card>

      <Card title={t.adminDashExportHeading} className={style.block}>
        <div className={style.exportRow}>
          <Text type="secondary" className={style.exportDesc}>{t.adminDashExportDescription}</Text>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
            className={style.btnSecondary}
          >
            {exporting ? t.adminDashExportingButton : t.adminDashExportButton}
          </Button>
        </div>
      </Card>
    </div>
  );

  return (
    <AdminLayout>
      {renderServicesContent()}
    </AdminLayout>
  );
}
